import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'qrcode';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiUsers, FiCamera, FiShare2, FiDownload, FiSmartphone, FiHeart, FiStar, FiArrowRight, FiCheck, FiCopy, FiMail, FiMessageSquare } = FiIcons;

const Home = () => {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeUseCase, setActiveUseCase] = useState(0);
  const [showQRDemo, setShowQRDemo] = useState(false);
  const [copied, setCopied] = useState(false);
  const qrCanvasRef = useRef(null);
  const mainQrCanvasRef = useRef(null);

  // Generate QR code for demo
  useEffect(() => {
    if (showQRDemo && qrCanvasRef.current) {
      QRCode.toCanvas(qrCanvasRef.current, window.location.origin, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
    }
  }, [showQRDemo]);

  // Generate QR code for main display
  useEffect(() => {
    if (mainQrCanvasRef.current) {
      QRCode.toCanvas(mainQrCanvasRef.current, window.location.origin, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
    }
  }, []);

  // Customer testimonials with generic scenarios
  const testimonials = [
    {
      id: 1,
      name: "Sarah & James",
      event: "Wedding Reception",
      location: "Lake District",
      quote: "Our guests shared over 300 photos instantly! We got candid moments we never would have seen otherwise. The QR codes on tables made it so easy.",
      rating: 5
    },
    {
      id: 2,
      name: "Marcus Chen",
      event: "Corporate Retreat",
      location: "Edinburgh",
      quote: "Perfect for team building events. Everyone could share their perspective of the day. The collaborative aspect really brought the team together.",
      rating: 5
    },
    {
      id: 3,
      name: "The Johnson Family",
      event: "Family Reunion",
      location: "Cornwall",
      quote: "Three generations sharing memories in one place! Grandparents could see photos instantly on their phones. Made our reunion unforgettable.",
      rating: 5
    },
    {
      id: 4,
      name: "Emma & Friends",
      event: "Hen Party Weekend",
      location: "Brighton",
      quote: "We created separate albums for each day - beach, spa, and night out. Everyone contributed and we have the most amazing collection now!",
      rating: 5
    }
  ];

  // Use cases with interactive examples
  const useCases = [
    {
      id: 1,
      title: "Weddings & Celebrations",
      description: "Create magical moments together",
      details: "From engagement parties to the big day, capture every smile, tear, and dance move from every guest's perspective.",
      scenarios: ["Wedding ceremonies", "Reception parties", "Engagement celebrations", "Anniversary parties"],
      icon: FiHeart
    },
    {
      id: 2,
      title: "Corporate Events",
      description: "Professional collaboration made simple",
      details: "Team building, conferences, and company retreats become more engaging when everyone can contribute their view.",
      scenarios: ["Team building days", "Annual conferences", "Product launches", "Office parties"],
      icon: FiUsers
    },
    {
      id: 3,
      title: "Family Gatherings",
      description: "Bringing generations together",
      details: "From birthday parties to family reunions, create shared albums that grandparents and grandchildren can both enjoy.",
      scenarios: ["Birthday celebrations", "Family reunions", "Holiday gatherings", "Graduation parties"],
      icon: FiCamera
    },
    {
      id: 4,
      title: "Group Adventures",
      description: "Share the journey, not just the destination",
      details: "Festivals, trips, and experiences are better when everyone's photos tell the complete story together.",
      scenarios: ["Music festivals", "Group holidays", "Adventure trips", "Sports events"],
      icon: FiShare2
    }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Demo photos for interactive showcase
  const demoPhotos = [
    { url: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400&h=300&fit=crop", uploaderName: "Sarah" },
    { url: "https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=600&fit=crop", uploaderName: "Mike" },
    { url: "https://images.unsplash.com/photo-1464207687429-7505649dae38?w=400&h=500&fit=crop", uploaderName: "Emma" },
    { url: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=400&h=400&fit=crop", uploaderName: "Tom" },
    { url: "https://images.unsplash.com/photo-1529636798458-92182e662485?w=400&h=350&fit=crop", uploaderName: "Lisa" }
  ];

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/#/share/demo-album`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleShareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Demo Album',
          text: 'Check out this album',
          url: `${window.location.origin}/#/share/demo-album`,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-hidden">
      {/* Hero Section - Enhanced */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left side - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center px-4 py-2 bg-black text-white rounded-full text-sm font-medium mb-8">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Free Beta Access - Limited Time
              </div>

              <div className="mb-8">
                <div className="text-gray-600 text-lg font-light mb-2 tracking-wide">
                  by Angry Branding
                </div>
                <h1 className="text-6xl sm:text-7xl lg:text-8xl font-light text-black tracking-tight mb-4">
                  Snap
                </h1>
                <div className="w-24 h-1 bg-black mx-auto lg:mx-0 mb-6"></div>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-gray-800 mb-6 leading-tight">
                Collaborative Event
                <span className="block text-black font-normal">Photo Sharing</span>
              </h2>

              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0 font-light leading-relaxed">
                Transform any event into a collaborative photography experience. From weddings to corporate retreats, 
                let everyone contribute their perspective and create something beautiful together.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
                <Link 
                  to="/signup" 
                  className="group bg-black text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-800 transition-all transform hover:scale-105 flex items-center justify-center"
                >
                  Start Creating Together
                  <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button 
                  onClick={() => scrollToSection('demo')}
                  className="group border-2 border-black text-black px-8 py-4 rounded-lg text-lg font-medium hover:bg-black hover:text-white transition-all flex items-center justify-center"
                >
                  <SafeIcon icon={FiPlay} className="mr-2" />
                  See How It Works
                </button>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-8 text-center lg:text-left">
                <div>
                  <div className="text-2xl font-light text-black mb-1">∞</div>
                  <div className="text-sm text-gray-600">Photos per event</div>
                </div>
                <div>
                  <div className="text-2xl font-light text-black mb-1">0s</div>
                  <div className="text-sm text-gray-600">Setup time</div>
                </div>
                <div>
                  <div className="text-2xl font-light text-black mb-1">100%</div>
                  <div className="text-sm text-gray-600">Free beta</div>
                </div>
              </div>
            </motion.div>

            {/* Right side - Interactive Demo */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-light text-black mb-2">Live Collaboration</h3>
                  <p className="text-gray-600 text-sm">Watch photos appear in real-time</p>
                </div>

                {/* Mini photo grid with animations */}
                <div className="columns-2 gap-3 space-y-3 mb-6">
                  {demoPhotos.map((photo, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.3 }}
                      className="break-inside-avoid mb-3 group cursor-pointer relative"
                    >
                      <div className="relative overflow-hidden rounded-lg bg-gray-100 shadow-sm">
                        <img 
                          src={photo.url} 
                          alt="" 
                          className="w-full h-auto object-cover transition-transform group-hover:scale-105" 
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-end p-2">
                          <div className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                            By {photo.uploaderName}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-4">
                  <span className="flex items-center">
                    <SafeIcon icon={FiUsers} className="mr-2" />
                    5 contributors
                  </span>
                  <span className="flex items-center">
                    <SafeIcon icon={FiCamera} className="mr-2" />
                    Live updates
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section - Interactive */}
      <section id="use-cases" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light text-black mb-6">Perfect For Every Occasion</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Whether it's an intimate gathering or a grand celebration, Snap brings people together through collaborative photography
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Use case tabs */}
            <div className="space-y-4">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={useCase.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  onClick={() => setActiveUseCase(index)}
                  className={`p-6 rounded-xl cursor-pointer transition-all transform hover:scale-105 ${
                    activeUseCase === index 
                      ? 'bg-black text-white shadow-xl' 
                      : 'bg-white hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      activeUseCase === index 
                        ? 'bg-white bg-opacity-20' 
                        : 'bg-black'
                    }`}>
                      <SafeIcon 
                        icon={useCase.icon} 
                        className={`w-6 h-6 ${activeUseCase === index ? 'text-white' : 'text-white'}`} 
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-medium mb-2">{useCase.title}</h3>
                      <p className={`text-sm ${activeUseCase === index ? 'text-gray-300' : 'text-gray-600'}`}>
                        {useCase.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Active use case details */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeUseCase}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 sticky top-8"
              >
                <div className="w-16 h-16 rounded-xl bg-black flex items-center justify-center mb-6">
                  <SafeIcon icon={useCases[activeUseCase].icon} className="w-8 h-8 text-white" />
                </div>
                
                <h3 className="text-2xl font-light text-black mb-4">{useCases[activeUseCase].title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{useCases[activeUseCase].details}</p>
                
                <div className="space-y-3">
                  <h4 className="font-medium text-black">Perfect for:</h4>
                  {useCases[activeUseCase].scenarios.map((scenario, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-black" />
                      <span className="text-gray-600">{scenario}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* How It Works - Interactive Demo */}
      <section id="demo" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light text-black mb-6">See It In Action</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Watch how easy it is to create, share, and collaborate on photo albums for any event
            </p>
          </motion.div>

          {/* Interactive demo steps */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                step: "01",
                title: "Create & Customize",
                description: "Set up your album in seconds with event details, custom descriptions, and privacy settings",
                icon: FiCamera
              },
              {
                step: "02", 
                title: "Share Instantly",
                description: "Generate QR codes and shareable links. Print table cards or send via WhatsApp, email, or SMS",
                icon: FiShare2
              },
              {
                step: "03",
                title: "Collaborate & Collect",
                description: "Watch photos appear in real-time as guests upload. Download everything with one click",
                icon: FiDownload
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="w-20 h-20 rounded-2xl bg-black flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-transform">
                  <SafeIcon icon={step.icon} className="w-10 h-10 text-white" />
                </div>
                <div className="text-sm text-gray-500 font-medium mb-2">{step.step}</div>
                <h3 className="text-xl font-medium text-black mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Sharing Demo Examples */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* QR Code Demo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiCamera} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-medium text-black mb-4">QR Code Sharing</h3>
                <p className="text-gray-600 mb-6 font-light">
                  Generate instant QR codes for table cards, invitations, or anywhere guests need quick access
                </p>
                
                <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
                  <canvas ref={mainQrCanvasRef} className="mx-auto mb-4"></canvas>
                  <p className="text-sm text-gray-500 font-light">Scan to visit Snap</p>
                </div>

                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  Print QR codes on table cards, invitations, or posters. Guests simply scan with their phone camera to access your album instantly.
                </p>
              </div>
            </motion.div>

            {/* Link Sharing Demo - Matches logged-in version with Device Sharing */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiShare2} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-medium text-black mb-4">Link Sharing</h3>
                <p className="text-gray-600 mb-6 font-light">
                  Share via WhatsApp, email, SMS, or any messaging platform with a simple link
                </p>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Share this link with your guests:
                  </label>
                  <div className="flex border border-gray-300 rounded-md overflow-hidden">
                    <input
                      type="text"
                      value={`${window.location.origin}/#/share/demo-album`}
                      readOnly
                      className="flex-1 px-4 py-3 bg-gray-50 text-sm font-mono text-gray-600 focus:outline-none"
                    />
                    <button
                      onClick={handleCopyLink}
                      className={`px-6 py-3 font-medium text-sm transition-colors ${
                        copied ? 'bg-green-600 text-white' : 'bg-black text-white hover:bg-gray-800'
                      }`}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {navigator.share && (
                    <button
                      onClick={handleShareNative}
                      className="block w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-center transition-all btn-secondary"
                    >
                      Share via Device
                    </button>
                  )}
                  <a
                    href={`mailto:?subject=Photo Album&body=${encodeURIComponent(`Check out this album: ${window.location.origin}/#/share/demo-album`)}`}
                    className="block w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-center transition-all btn-secondary"
                  >
                    Share via Email
                  </a>
                  <a
                    href={`sms:?body=${encodeURIComponent(`Check out this album: ${window.location.origin}/#/share/demo-album`)}`}
                    className="block w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-center transition-all btn-secondary"
                  >
                    Share via SMS
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Mock interface showcase */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gray-50 rounded-2xl p-8 shadow-inner"
          >
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl mx-auto">
              <div className="text-center mb-8 pb-6 border-b border-gray-200">
                <h3 className="text-2xl font-light text-black mb-2">Sarah & Mike's Wedding</h3>
                <p className="text-gray-600 font-light">Share your photos from our special day!</p>
                <div className="flex justify-center items-center gap-4 mt-4 text-sm text-gray-500">
                  <span className="bg-gray-100 px-3 py-1 rounded-full">127 photos</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full">23 contributors</span>
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full">Live</span>
                </div>
              </div>

              {/* Mock upload area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-8 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
                <div className="text-center">
                  <SafeIcon icon={FiSmartphone} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-light text-black mb-2">Upload Your Photos</h4>
                  <p className="text-gray-600 text-sm font-light mb-4">
                    Drag and drop your files here, or tap to browse
                  </p>
                  <div className="bg-black text-white px-6 py-2 rounded-md text-sm font-medium inline-block">
                    Choose Files
                  </div>
                </div>
              </div>

              {/* Mock photo grid */}
              <div className="columns-2 sm:columns-3 gap-4 space-y-4">
                {demoPhotos.map((photo, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="break-inside-avoid mb-4 group cursor-pointer relative"
                  >
                    <div className="relative overflow-hidden rounded-lg bg-gray-100 shadow-sm hover:shadow-md transition-all">
                      <img
                        src={photo.url}
                        alt=""
                        className="w-full h-auto object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-end p-3">
                        <div className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                          By {photo.uploaderName}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Customer Testimonials - Interactive Carousel */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light mb-6">Real Stories, Real Results</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light">
              See how Snap has transformed events and brought people together
            </p>
          </motion.div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 max-w-4xl mx-auto"
              >
                <div className="flex flex-col items-center gap-8 text-center">
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-xl font-light mb-6 leading-relaxed max-w-3xl">
                    "{testimonials[activeTestimonial].quote}"
                  </blockquote>
                  <div>
                    <div className="font-medium text-lg">{testimonials[activeTestimonial].name}</div>
                    <div className="text-gray-300 text-sm">
                      {testimonials[activeTestimonial].event} • {testimonials[activeTestimonial].location}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Testimonial indicators */}
            <div className="flex justify-center space-x-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    activeTestimonial === index 
                      ? 'bg-white' 
                      : 'bg-white bg-opacity-30 hover:bg-opacity-50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid - Enhanced */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light text-black mb-6">Everything You Need</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Powerful features designed for seamless collaboration and beautiful results
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FiUsers,
                title: "Unlimited Collaborators",
                description: "No limits on how many people can contribute photos to your event albums"
              },
              {
                icon: FiSmartphone,
                title: "QR Code Magic",
                description: "Generate instant QR codes for tables, invitations, or anywhere you need quick access"
              },
              {
                icon: FiCamera,
                title: "Real-time Updates", 
                description: "Watch photos appear instantly as guests upload them throughout your event"
              },
              {
                icon: FiDownload,
                title: "Bulk Download",
                description: "Download all photos from your event in one click, perfectly organized"
              },
              {
                icon: FiShare2,
                title: "Smart Sharing",
                description: "Share via link, QR code, email, SMS, or any social platform"
              },
              {
                icon: FiHeart,
                title: "No Registration Required",
                description: "Guests can upload photos without creating accounts or downloading apps"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-medium text-black mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA - Enhanced */}
      <section className="py-20 bg-gradient-to-r from-black to-gray-900 text-white relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-white bg-opacity-10 rounded-full text-sm font-medium mb-8 backdrop-blur-sm">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
              Join the Beta - Limited Spots Available
            </div>

            <h2 className="text-4xl sm:text-5xl font-light mb-6 leading-tight">
              Ready to Transform Your
              <span className="block font-normal">Next Event?</span>
            </h2>

            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto font-light leading-relaxed">
              Join thousands of event creators who are already using Snap to capture memories together. 
              Create your first collaborative album in under 30 seconds.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link 
                to="/signup" 
                className="group bg-white text-black px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-all transform hover:scale-105 flex items-center justify-center"
              >
                Start Your Free Album
                <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to="/login" 
                className="border-2 border-white border-opacity-30 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-white hover:bg-opacity-10 transition-all backdrop-blur-sm flex items-center justify-center"
              >
                Sign In
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { label: "Setup Time", value: "30 seconds" },
                { label: "Cost", value: "Free Beta" },
                { label: "Contributors", value: "Unlimited" },
                { label: "Storage", value: "500MB per file" }
              ].map((stat, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full mb-2"></div>
                  <div className="text-sm text-gray-300 mb-1">{stat.label}</div>
                  <div className="font-medium">{stat.value}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer - Enhanced */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <span className="text-2xl font-light text-black">Snap</span>
                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  Beta
                </span>
              </div>
              <p className="text-gray-600 mb-6 font-light leading-relaxed">
                Collaborative event photography made beautiful and simple. 
                Created by Angry Branding for event organizers who want to capture every moment together.
              </p>
              <div className="text-sm text-gray-500">
                © 2025 Snap by{' '}
                <a 
                  href="https://www.angrybranding.com" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-black hover:underline font-medium"
                >
                  Angry Branding
                </a>
                . All rights reserved.
              </div>
            </div>

            <div>
              <h3 className="font-medium text-black mb-4">Product</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li><Link to="/signup" className="hover:text-black transition-colors">Create Album</Link></li>
                <li><button onClick={() => scrollToSection('use-cases')} className="hover:text-black transition-colors">Use Cases</button></li>
                <li><button onClick={() => scrollToSection('demo')} className="hover:text-black transition-colors">How It Works</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-black mb-4">Support</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li>
                  <button 
                    onClick={() => setShowPrivacyPolicy(true)} 
                    className="hover:text-black transition-colors"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <a 
                    href="https://www.angrybranding.com" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="hover:text-black transition-colors"
                  >
                    Contact Us
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.angrybranding.com" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="hover:text-black transition-colors"
                  >
                    About Angry Branding
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <PrivacyPolicyModal onClose={() => setShowPrivacyPolicy(false)} />
      )}

      {/* QR Demo Modal */}
      {showQRDemo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg max-w-md w-full p-8"
          >
            <div className="text-center">
              <h2 className="text-2xl font-light text-black mb-6">QR Code Demo</h2>
              <div className="bg-gray-50 p-8 rounded-lg mb-6 inline-block">
                <canvas ref={qrCanvasRef} className="block mx-auto"></canvas>
              </div>
              <p className="text-gray-600 text-sm mb-8 font-light leading-relaxed">
                Print QR codes on table cards, invitations, or posters. Guests simply scan with their phone camera to access your album instantly.
              </p>
              <button
                onClick={() => setShowQRDemo(false)}
                className="bg-black text-white px-6 py-3 rounded-md hover:bg-gray-800 transition-all font-medium"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Home;